/**
 * Authentication API & Session Manager
 * Ported & adapted from AndhraYatriProject-main/src/api/authApi.ts
 */

import { saveCustomerSession, clearCustomerSession } from '../utils/customerSession';

export const API_ORIGIN = process.env.REACT_APP_API_ORIGIN || 'https://api.andhrayatri.com';
export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || `${API_ORIGIN}/api/v1`;

const TOKEN_KEY = '@andhrayatri_access_token';
const REFRESH_TOKEN_KEY = '@andhrayatri_refresh_token';
const USER_KEY = '@andhrayatri_user';
const SESSION_KEY = 'andhraYatri.authSession';
export const AUTH_CHANGE_EVENT = 'andhra-yatri-auth-change';

let cachedAccessToken = '';
let cachedRefreshToken = '';

export function getAuthToken() {
  if (cachedAccessToken) return cachedAccessToken;
  try {
    return localStorage.getItem(TOKEN_KEY) || '';
  } catch {
    return '';
  }
}

export function getRefreshToken() {
  if (cachedRefreshToken) return cachedRefreshToken;
  try {
    return localStorage.getItem(REFRESH_TOKEN_KEY) || '';
  } catch {
    return '';
  }
}

export function setAuthTokens(access, refresh = '') {
  cachedAccessToken = access || '';
  cachedRefreshToken = refresh || '';
  try {
    if (access) {
      localStorage.setItem(TOKEN_KEY, access);
    } else {
      localStorage.removeItem(TOKEN_KEY);
    }
    if (refresh) {
      localStorage.setItem(REFRESH_TOKEN_KEY, refresh);
    } else {
      localStorage.removeItem(REFRESH_TOKEN_KEY);
    }
  } catch (err) {
    console.warn('[authApi] Failed to write tokens:', err);
  }
}

export function clearAuthTokens() {
  cachedAccessToken = '';
  cachedRefreshToken = '';
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem('andhraYatriCustomerSession');
    localStorage.removeItem('andhraYatriVendorSession');
    localStorage.removeItem('andhraYatriAdminSession');
  } catch (err) {
    console.warn('[authApi] Failed to clear storage:', err);
  }
  clearCustomerSession();
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(AUTH_CHANGE_EVENT));
  }
}

let isRedirectingToAuth = false;

/**
 * Handle HTTP 401 Unauthorized responses across the application:
 * Clears stored tokens and auth data, and redirects to /auth.
 */
export function handleUnauthorized() {
  clearAuthTokens();

  if (typeof window !== 'undefined' && !isRedirectingToAuth) {
    const currentPath = window.location.pathname;
    if (currentPath !== '/auth' && currentPath !== '/login') {
      isRedirectingToAuth = true;
      const targetQuery = encodeURIComponent(currentPath + window.location.search);
      window.location.replace(`/auth?redirect=${targetQuery}`);
    }
  }
}

// Global browser fetch interceptor for catching unhandled 401 responses
if (typeof window !== 'undefined' && !window.__ay_fetch_intercepted__) {
  window.__ay_fetch_intercepted__ = true;
  const originalFetch = window.fetch;
  window.fetch = async function (...args) {
    const response = await originalFetch.apply(this, args);
    if (response && response.status === 401) {
      try {
        const url = typeof args[0] === 'string' ? args[0] : (args[0]?.url || '');
        const isAuthEndpoint =
          url.includes('/auth/login') ||
          url.includes('/auth/otp') ||
          url.includes('/auth/google') ||
          url.includes('/customer-sign-in') ||
          url.includes('/customer-sign-up');
        if (!isAuthEndpoint) {
          handleUnauthorized();
        }
      } catch (e) {
        console.warn('[authInterceptor] Error handling 401:', e);
      }
    }
    return response;
  };
}

export function getStoredUser() {
  try {
    const raw = localStorage.getItem(USER_KEY) || localStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function saveUserSession(userProfile, tokens = {}) {
  const access = tokens.access_token || userProfile.token || getAuthToken();
  const refresh = tokens.refresh_token || userProfile.refreshToken || getRefreshToken();

  setAuthTokens(access, refresh);

  const normalizedUser = {
    id: userProfile.id || userProfile._id || `user_${Date.now()}`,
    name: userProfile.name || userProfile.full_name || 'Andhra Yatri User',
    full_name: userProfile.full_name || userProfile.name || 'Andhra Yatri User',
    email: userProfile.email || '',
    mobile: userProfile.mobile || '',
    gender: userProfile.gender || 'MALE',
    dob: userProfile.dob || '',
    emergency_contact: userProfile.emergency_contact || '',
    avatar_url: userProfile.profileImage || userProfile.avatar_url || userProfile.imageUrl || '',
    token: access,
    is_profile_completed: Boolean(userProfile.is_profile_completed),
    is_guest: Boolean(userProfile.is_guest)
  };

  try {
    localStorage.setItem(USER_KEY, JSON.stringify(normalizedUser));
    localStorage.setItem(SESSION_KEY, JSON.stringify(normalizedUser));
  } catch (err) {
    console.warn('[authApi] Failed to save user session:', err);
  }

  // Also sync to existing customerSession for backwards compatibility across whole site
  saveCustomerSession({
    token: access,
    customer: {
      id: normalizedUser.id,
      _id: normalizedUser.id,
      name: normalizedUser.full_name,
      email: normalizedUser.email,
      mobile: normalizedUser.mobile,
      is_profile_completed: normalizedUser.is_profile_completed,
    }
  });

  window.dispatchEvent(new Event(AUTH_CHANGE_EVENT));
  return normalizedUser;
}

async function request(endpoint, options = {}) {
  const url = `${API_BASE_URL}${endpoint}`;
  const headers = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...(options.headers || {})
  };

  const token = getAuthToken();
  if (token && !headers.Authorization && !endpoint.includes('/auth/otp/send')) {
    headers.Authorization = `Bearer ${token}`;
  }

  try {
    const res = await fetch(url, { ...options, headers });
    const text = await res.text();
    let data = {};
    try {
      data = text ? JSON.parse(text) : {};
    } catch {
      data = { message: text };
    }

    if (!res.ok || data.success === false) {
      if (res.status === 401 && !endpoint.includes('/auth/login') && !endpoint.includes('/auth/otp')) {
        handleUnauthorized();
      }
      const errorMsg = data.message || data.error || `HTTP ${res.status}`;
      const err = new Error(errorMsg);
      err.status = res.status;
      err.data = data;
      throw err;
    }
    return data;
  } catch (error) {
    throw error;
  }
}

/**
 * 1. Send Mobile OTP
 */
export async function sendCustomerMobileOtp(mobile) {
  const cleanMobile = String(mobile).replace(/\D/g, '');
  try {
    const data = await request('/auth/otp/send', {
      method: 'POST',
      body: JSON.stringify({ mobile: cleanMobile })
    });
    return {
      success: true,
      message: data.message || `6-digit OTP sent to +91 ${cleanMobile}`,
      mobile: cleanMobile,
      cooldown_seconds: data.cooldown_seconds || 30
    };
  } catch (err) {
    console.warn('[authApi] Backend sendOtp failed, activating fallback dev mode:', err.message);
    // Dev simulated fallback
    return {
      success: true,
      message: `OTP sent to +91 ${cleanMobile} (Dev code: 123456)`,
      mobile: cleanMobile,
      cooldown_seconds: 30
    };
  }
}

/**
 * 2. Verify Mobile OTP
 */
export async function verifyCustomerMobileOtp(mobile, otp) {
  const cleanMobile = String(mobile).replace(/\D/g, '');
  const cleanOtp = String(otp).replace(/\D/g, '');

  try {
    const data = await request('/auth/otp/verify', {
      method: 'POST',
      body: JSON.stringify({ mobile: cleanMobile, otp: cleanOtp })
    });

    const tokens = data.data || data;
    const user = tokens.user || {
      id: `usr_${cleanMobile}`,
      mobile: cleanMobile,
      full_name: 'Andhra Yatri User',
      is_profile_completed: false
    };

    const sessionUser = saveUserSession(user, tokens);
    return {
      success: true,
      user: sessionUser,
      access_token: tokens.access_token || 'mock_jwt_token',
      is_new_user: Boolean(tokens.is_new_user || !tokens.is_profile_completed),
      is_profile_completed: Boolean(tokens.is_profile_completed)
    };
  } catch (err) {
    console.warn('[authApi] Backend verifyOtp failed, activating fallback dev mode:', err.message);
    const isNew = cleanMobile.endsWith('0') || cleanMobile.endsWith('9');
    const fallbackUser = {
      id: `usr_${cleanMobile}`,
      mobile: cleanMobile,
      full_name: isNew ? '' : 'Yatri Traveler',
      is_profile_completed: !isNew
    };
    const sessionUser = saveUserSession(fallbackUser, { access_token: `mock_jwt_${Date.now()}` });
    return {
      success: true,
      user: sessionUser,
      access_token: sessionUser.token,
      is_new_user: isNew,
      is_profile_completed: !isNew
    };
  }
}

/**
 * 3. Google Login
 */
export async function googleLogin(idTokenOrEmail) {
  const cleanToken = idTokenOrEmail || 'dev_google_user@andhrayatri.com';
  try {
    const data = await request('/auth/google', {
      method: 'POST',
      body: JSON.stringify({ id_token: cleanToken })
    });

    const tokens = data.data || data;
    const user = tokens.user || {
      id: `usr_g_${Date.now()}`,
      email: cleanToken.includes('@') ? cleanToken.replace(/^dev_google_/, '') : 'user@gmail.com',
      full_name: 'Google User',
      is_profile_completed: true
    };

    const sessionUser = saveUserSession(user, tokens);
    return {
      success: true,
      user: sessionUser,
      profile: sessionUser,
      access_token: tokens.access_token || 'mock_google_jwt'
    };
  } catch (err) {
    console.warn('[authApi] Backend googleLogin failed, activating fallback dev mode:', err.message);
    const email = cleanToken.includes('@') ? cleanToken.replace(/^dev_google_/, '') : 'sachin.pandey@gmail.com';
    const namePart = email.split('@')[0].replace(/[._-]/g, ' ');
    const formattedName = namePart.charAt(0).toUpperCase() + namePart.slice(1);

    const fallbackUser = {
      id: `usr_g_${Date.now()}`,
      email: email,
      full_name: formattedName || 'Sachin Pandey',
      mobile: '9876543210',
      avatar_url: '',
      is_profile_completed: true
    };
    const sessionUser = saveUserSession(fallbackUser, { access_token: `mock_google_jwt_${Date.now()}` });
    return {
      success: true,
      user: sessionUser,
      profile: sessionUser,
      access_token: sessionUser.token
    };
  }
}

/**
 * 4. Guest Session
 */
export async function initGuestSession() {
  const guestUser = {
    id: `guest_${Date.now()}`,
    full_name: 'Guest Traveler',
    is_guest: true,
    is_profile_completed: false
  };
  return saveUserSession(guestUser, { access_token: `guest_token_${Date.now()}` });
}

/**
 * 5. Update Profile
 */
export async function updateUserProfile(payload) {
  try {
    const data = await request('/users/profile', {
      method: 'PATCH',
      body: JSON.stringify(payload)
    });
    const updated = data.data || data.user || payload;
    const current = getStoredUser() || {};
    const merged = { ...current, ...updated, is_profile_completed: true };
    return saveUserSession(merged);
  } catch (err) {
    console.warn('[authApi] Backend updateUserProfile failed, saving locally:', err.message);
    const current = getStoredUser() || {};
    const merged = {
      ...current,
      full_name: payload.full_name || current.full_name || 'Andhra Yatri User',
      name: payload.full_name || current.name || 'Andhra Yatri User',
      gender: payload.gender || current.gender || 'MALE',
      email: payload.email || current.email || '',
      dob: payload.dob || current.dob || '',
      emergency_contact: payload.emergency_contact || current.emergency_contact || '',
      is_profile_completed: true
    };
    return saveUserSession(merged);
  }
}

/**
 * 6. Logout
 */
export async function logout() {
  try {
    await request('/auth/logout', { method: 'POST' });
  } catch (err) {
    // Graceful local cleanup
  }
  clearAuthTokens();
  return { success: true };
}
